import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { FC, useEffect } from 'react';
import { IconButton, Typography } from '@mui/material';
import { palette } from '../../theme';

dayjs.extend(isBetweenPlugin);
dayjs.extend(isoWeek);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isLastDay && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})) as React.ComponentType<CustomPickerDayProps>;

const COEDateRangeComponent: FC<any> = (props) => {
  const [open, setOpen] = React.useState(false);
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const incramentWeek = (step: number) => {
    props.setValue(props.value.add(step, 'week'));
  };

  useEffect(() => {
    const start = props.value.startOf('isoWeek');
    const end = props.value.endOf('isoWeek');
    setStartDate(start.format('MMM D'));
    setEndDate(start.month() === end.month() ? end.format('D') : end.format('MMM D'));
  }, [props.value]);

  const renderWeekPickerDay = (
    date: Dayjs,
    selectedDates: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    if (!props.value) {
      return <PickersDay {...pickersDayProps} />;
    }

    const start = props.value.startOf('isoWeek');
    const end = props.value.endOf('isoWeek');

    const dayIsBetween = date.isBetween(start, end, null, '[]');
    const isFirstDay = date.isSame(start, 'day');
    const isLastDay = date.isSame(end, 'day');
    setStartDate(start.format('MMM D'));
    setEndDate(start.month() === end.month() ? end.format('D') : end.format('MMM D'));

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Week"
        value={props.value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={props.disabled}
        onChange={(newValue) => {
          props.setValue(newValue);
        }}
        renderDay={renderWeekPickerDay}
        renderInput={(params) => (
          <div
            ref={params.inputRef}
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: palette.grey[100],
              borderRadius: 20,
              height: 36,
              padding: '0 4px',
            }}
          >
            <IconButton onClick={() => incramentWeek(-1)} disabled={props.disabled} size="small" style={{ padding: 4 }}>
              <KeyboardArrowLeft style={{ color: palette.text.primary, fontSize: 20 }} />
            </IconButton>
            <Typography
              style={{
                color: palette.text.primary,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                padding: '0 8px',
                userSelect: 'none',
              }}
              onClick={() => setOpen(true)}
            >
              {`${startDate} – ${endDate}`}
            </Typography>
            <IconButton onClick={() => incramentWeek(1)} disabled={props.disabled} size="small" style={{ padding: 4 }}>
              <KeyboardArrowRight style={{ color: palette.text.primary, fontSize: 20 }} />
            </IconButton>
          </div>
        )}
        disableMaskedInput
      />
    </LocalizationProvider>
  );
};

export default COEDateRangeComponent;
